import "./ChatGPT.css";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import API_ENDPOINTS from "../../apis/endpoints";
import SendIcon from "@mui/icons-material/Send";
import Bot from "../../assets/icons/support.svg";
import useAuthApi from "../../context/hooks";
import {
    Avatar,
    Button,
    Container,
    Skeleton,
    styled,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import useAuth from "../../context";

const StyledAvatar = styled(Avatar)(({ theme }) => ({
    width: theme.spacing(5),
    height: theme.spacing(5),

    boxShadow: "0 0 0 4px rgba(255, 255, 255, 0.2)",
    backgroundColor: theme.palette.graph.cyan,
    marginTop: "-15px",
    marginLeft: "20px",
}));

const ChatGPT = () => {
    const executeApiRequest = useAuthApi();
    const auth = useAuth();
    const theme = useTheme();
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(true); // Show suggestions initially
    const storedDeviceId = localStorage.getItem("device-id");

    // Suggestions array
    const suggestions = [
        "Who are the main actors in the movie: ",
        // "What is the category of the brand: ",
        "What is the lifetime collection of the movie: ",
        // "Tell me about the brand: ",
    ];

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const clearChatContext = async () => {
        setShowSuggestions(true);
        try {
            await fetch(`${API_ENDPOINTS.CHAT_GPT}clear-context/`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": storedDeviceId,
                },
                body: JSON.stringify({}),
            });
            // Reset messages after clearing context
            setMessages([]);
        } catch (error) {
            console.error("Error clearing chat context:", error);
        }
    };

    const handleSendMessage = async () => {
        setShowSuggestions(false);

        if (inputValue.trim() === "" || isLoading) return;

        const newMessage = { text: inputValue, isUser: true };
        setMessages([...messages, newMessage]);
        setInputValue("");
        setIsLoading(true);

        try {
            const result = await fetchApiResponse(inputValue);

            setMessages((prevMessages) => [
                ...prevMessages,
                {
                    text: formatMessage(result.data.response),
                    isUser: false,
                    error: result.error,
                    error_type: result.error_type,
                },
            ]);

            // Hide suggestions after first message
            setShowSuggestions(false);
        } catch (error) {
            console.error("Error fetching response:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Function to truncate text
    const truncateText = (text, maxLength) => {
        if (text.length > maxLength) {
            return text.substring(0, maxLength) + "...";
        }
        return text;
    };

    const formatMessage = (msg) => {
        msg = String(msg);
        msg = msg.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
        msg = msg.replace(/### (.*?)\n/g, "<h3>$1</h3>");
        msg = msg.replace(/\n/g, "<br />");
        return msg;
    };

    const handleSuggestionClick = (suggestion) => {
        setInputValue(suggestion);
        textareaRef.current.focus(); // Set focus back to textarea
    };

    const fetchApiResponse = async (query) => {
        try {
            const result = await executeApiRequest({
                field: "query",
                value: query,
                apiEndpoint: `${API_ENDPOINTS.CHAT_GPT}search/`,
                method: "POST",
                preprocessData: (field, value) => {
                    return {
                        query: value,
                    };
                },
                headers: { "Content-Type": "application/json" },
            });

            if (result.data && result.data.response) {
                return result;
            } else {
                throw new Error("No response data");
            }
        } catch (error) {
            console.error("Error in fetchApiResponse:", error);
            throw error;
        }
    };

    const renderMessages = () => {
        if (messages.length === 0) {
            return (
                <div className="bot-chat-messages">
                    {" "}
                    <div className="icon-border" style={{ marginTop: "10px" }}>
                        {" "}
                        <img src={Bot} alt="Bot" />{" "}
                    </div>{" "}
                    <div className="bot-message">
                        {" "}
                        <Typography
                            variant="highlightSemibold"
                            sx={{
                                marginBottom: "20px",
                                color: theme.palette.neutral[400],
                            }}
                        >
                            {" "}
                            Welcome {auth.user.first_name} {auth.user.last_name}{" "}
                        </Typography>{" "}
                        <Typography variant="contentRegular">
                            {" "}
                            Hello! Let me know what you're looking for or ask me
                            anything about the product. Simply type your query
                            or select questions from the list and hit enter.{" "}
                        </Typography>{" "}
                    </div>{" "}
                </div>
            );
        }

        return (
            <AnimatePresence>
                {messages.map((message, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className={`message-container ${message.isUser ? "user" : "bot"}`}
                    >
                        {/* If the message is from the user */}
                        {message.isUser ? (
                            <>
                                <div
                                    className={`${message.isUser ? "user-message" : "bot-message"}`}
                                >
                                    <Typography
                                        variant="contentAccent"
                                        sx={{
                                            marginTop: "8px",
                                            color: theme.palette.neutral[500],
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: message.text,
                                        }}
                                    />
                                </div>
                                <div className="icon-container">
                                    <StyledAvatar
                                        alt={auth.user.first_name}
                                        src={auth.user.picture}
                                    />
                                </div>
                            </>
                        ) : (
                            // If the message is from the bot
                            <>
                                <div className="icon-container">
                                    <div
                                        className="icon-border"
                                        style={{
                                            backgroundColor:
                                                theme.palette.primary[500],
                                            marginLeft: "20px",
                                            marginTop: "30px",
                                        }}
                                    >
                                        <img src={Bot} alt="Bot" style={{}} />
                                    </div>
                                </div>
                                <div
                                    className={`${message.isUser ? "user-message" : "bot-message"}`}
                                >
                                    <Typography
                                        variant="contentRegular"
                                        sx={{
                                            marginTop: "8px",
                                            color: theme.palette.neutral[400],
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: message.text,
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </motion.div>
                ))}
                {isLoading && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="message-container bot"
                    >
                        <div className="icon-container">
                            <img src={Bot} alt="Bot" />
                        </div>
                        <Skeleton
                            variant="text"
                            width="60%"
                            height={84}
                            style={{
                                marginTop: "-20px",
                                borderRadius: "18px",
                                boxShadow: "0 1px 2px rgba(20, 20, 20, 0.2)",
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        );
    };

    return (
        <Container style={{ padding: 0, width: "100%" }}>
            <div className="chat-container">
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    {" "}
                    <Typography
                        variant="featureAccent"
                        sx={{ marginBottom: "20px" }}
                    >
                        Ask me Anything
                    </Typography>
                    <Button
                        onClick={clearChatContext}
                        style={{
                            marginBottom: "20px",
                            padding: "10px",
                            backgroundColor: theme.palette.primary.main,
                            color: "#fff",
                            borderRadius: "5px",
                            cursor: "pointer",
                            width: "20%",
                            alignSelf: "flex-end",
                        }}
                    >
                        {" "}
                        New Chat{" "}
                    </Button>{" "}
                </div>
                <div className="chat-messages">
                    {" "}
                    {renderMessages()} <div ref={messagesEndRef} />{" "}
                    {/* Suggestions Section */}{" "}
                    {showSuggestions && (
                        <div className="suggestions">
                            {" "}
                            {suggestions.map((suggestion, index) => (
                                <motion.button
                                    key={index}
                                    className="suggestion-button"
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() =>
                                        handleSuggestionClick(suggestion)
                                    }
                                >
                                    {" "}
                                    {suggestion}{" "}
                                </motion.button>
                            ))}{" "}
                        </div>
                    )}{" "}
                </div>{" "}
                <div className="input-wrapper">
                    {" "}
                    <textarea
                        ref={textareaRef}
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder="Type Your Text Here"
                        disabled={isLoading}
                        onKeyPress={(e) => {
                            if (e.key === "Enter") handleSendMessage();
                        }}
                    />{" "}
                    <motion.button
                        className="send-button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleSendMessage}
                        disabled={isLoading || inputValue.trim() === ""}
                    >
                        {" "}
                        {isLoading ? (
                            <div className="loader"></div>
                        ) : (
                            <SendIcon />
                        )}{" "}
                    </motion.button>{" "}
                </div>{" "}
            </div>
        </Container>
    );
};

export default ChatGPT;
